import React, { useRef, useState } from 'react';
import adsApi from './adsApi';

/** Resolves stored ad image paths for admin preview (Vite proxies `/images` → Next). */
export function adsImageSrc(path) {
  const p = String(path || '').trim();
  if (!p) return '';
  if (/^https?:\/\//i.test(p) || p.startsWith('data:')) return p;
  return p.startsWith('/') ? p : `/${p}`;
}

/**
 * Upload-or-paste image field for Brand Ads creatives.
 */
export default function AdsImageField({
  label,
  value,
  onChange,
  onError,
  accept = 'image/jpeg,image/png,image/webp,image/gif',
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await adsApi.uploadImage(file);
      const path = res?.data?.path;
      if (!path) throw new Error('Upload succeeded but no path returned');
      onChange(path);
    } catch (err) {
      onError?.(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const preview = adsImageSrc(value);

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-slate-500">{label}</label>
      <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3">
        {preview ? (
          <div className="space-y-2">
            <img
              src={preview}
              alt={label}
              className="max-h-36 w-full rounded object-contain bg-white"
            />
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-100"
                disabled={uploading}
                onClick={() => inputRef.current?.click()}
              >
                {uploading ? 'Uploading…' : 'Replace'}
              </button>
              <button
                type="button"
                className="rounded border border-red-200 bg-white px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                disabled={uploading}
                onClick={() => onChange('')}
              >
                Remove
              </button>
            </div>
            <p className="truncate text-[11px] text-slate-400" title={value}>
              {value}
            </p>
          </div>
        ) : (
          <button
            type="button"
            className="flex w-full flex-col items-center gap-1 py-6 text-slate-500 hover:text-slate-800 disabled:opacity-60"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            <i className={`fas ${uploading ? 'fa-spinner fa-spin' : 'fa-cloud-upload-alt'} text-2xl`} />
            <span className="text-sm">{uploading ? 'Uploading…' : 'Click to upload image'}</span>
            <span className="text-[11px] text-slate-400">JPG, PNG, GIF → saved as WebP · max 5MB</span>
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => {
            void handleFile(e.target.files?.[0]);
          }}
        />
      </div>
      <input
        className="w-full rounded border border-slate-300 px-2 py-1.5 text-xs text-slate-700"
        placeholder="Or paste image URL / path"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
